<script lang="ts" setup>
const props = defineProps<{ recordId: string, recordName: string, collection: string, averageRating: Rating }>()
const { loggedIn, isUsers, admin } = useUser()
const pb = useNuxtApp().$pb
const addingReview = ref(false)
const fullCollection = GetCollection(props.collection)
const bId = computed(() => props.recordId)

const { data: reviews, status, refresh } = await useAsyncData(
  `${props.collection}-allreview-${props.recordId}`,
  async () => {
    return await pb.collection('allReviews').getList<Review>(1, 9999, {
      filter: pb.filter(`reviewedId = {:id} && reviewedEntity = {:collection}`, {
        id: props.recordId,
        collection: props.collection,
      }),
    })
  },
  {
    server: true,
    watch: [bId],
  },
)
async function initAddingReview() {
  addingReview.value = true
}

function newReviewAdded() {
  addingReview.value = false
  refresh()
}

function reviewUpdated(review: Review) {
  if (reviews?.value) {
    const index = reviews.value?.items.findIndex((r: Review) => r.id === review.id)
    if (index !== -1) {
      reviews.value.items[index] = { ...reviews.value.items[index], ...review }
    }
  }
  addingReview.value = false
}

const isUsersItem = computed(() => props.recordId ? isUsers(props.recordId, props.collection) : false)

const reviewString = computed(() => {
  if (fullCollection.collection === 'businesses') {
    return `Have you done business with ${props.recordName}?`
  }
  return `Would you like to review this ${fullCollection.singular}?`
})
</script>

<template>
  <div class=" rounded-2xl overflow-clip shadow-inner py-12">
    <div class="px-6 md:px-8 lg:px-12 py-12 flex flex-wrap justify-center gap-12 lg:gap-x-32 max-w-screen-2xl mx-auto">
      <div
        v-if="reviews && reviews.totalItems > 0"
        class="flex flex-wrap justify-between gap-6 w-full items-center max-w-7xl mx-auto"
      >
        <CollectionBusinessRating
          :rating="averageRating ?? 0"
          class="text-yellow"
        />
        <div class="flex gap-6 ml-auto items-center">
          <div
            class="p-2 rounded-full bg-primary-600 text-white text-lg md:text-xl lg:text-2xl h-8 min-w-8 md:min-w-12 md:h-12 lg:h-16 lg:min-w-16 flex items-center justify-center"
          >
            {{ reviews.totalItems }}
          </div>
          <HeadingText tag="h3">
            Review{{ reviews.totalItems === 1 ? '' : 's' }}
          </HeadingText>
        </div>
      </div>
    </div>
    <section class="flex flex-wrap gap-12 justify-center w-full">
      <CollectionReviewCard
        v-for="review in reviews?.items"
        :key="review.id"
        :data="review"
        :business-name="recordName"
        class="min-w-md max-w-xl relative z-20"
        @updated="newReviewAdded()"
      />

      <div
        v-if="!isUsersItem"
        class="bg-yellow relative px-6 py-12 md:px-8 md:py-16 lg:p-10 lg:py-20 min-w-md rounded-t-3xl rounded-r-3xl md:rounded-t-4xl md:rounded-r-4xl text-primary-900 shadow flex-1 max-w-xl text-lg md:text-xl lg:text-2xl text-center flex items-center justify-center"
      >
        <div class="bg-yellow animate-ping-sm z-10 absolute inset-0 rounded-t-3xl rounded-r-3xl md:rounded-t-4xl md:rounded-r-4xl opacity-30" />
        <div class="flex flex-col items-center justify-center z-20">
          <p class="font-display font-bold text-xl md:text-2xl lg:text-3xl h-full w-full">
            {{ reviewString }}
          </p>
          <UButton
            v-if="loggedIn && !admin"
            variant="solid"
            color="gray"
            class="mt-6 gap-2 flex items-center"
            size="xl"
            @click="initAddingReview"
          >
            <Icon
              name="ri:pencil-line"
              class="w-6 h-6  text-lightblue animate-pulse"
            /> Leave A Review
          </UButton>
          <UAlert
            v-else-if="admin"
            description="Admins cannot leave a review. Please login as a user."
            color="red"
            class="mt-3"
          />
          <UButton
            v-else
            variant="solid"
            color="gray"
            class="mt-6 gap-2 flex items-center"
            size="xl"
            to="/login"
          >
            <Icon
              name="ri:pencil-line"
              class="w-6 h-6  text-lightblue animate-pulse"
            /> Login to leave a review
          </UButton>
          <UModal
            v-model="addingReview"
            class="w-full"
            :ui="{ rounded: 'rounded-bl-none rounded-t-3xl rounded-br-3xl overflow-clip' }"
          >
            <CollectionReviewCreate
              :id="recordId"
              :collection="collection"
              :business="recordId"
              @created="newReviewAdded()"
              @update="reviewUpdated"
            />
          </UModal>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.animate-ping-sm{
  animation:  ping-sm 1s cubic-bezier(0, 0, 0.5, 1) infinite

}

@keyframes ping-sm {
  75%, 100% {
    transform: scale(1.3);
    opacity: 0;
  }
}
</style>
